export const INSIGHTS = [
  {
    slug: "estrategia-seo-conectada-ao-negocio",
    category: "Estratégia SEO",
    title: "Como funciona uma estratégia de SEO conectada ao negócio",
    excerpt: "Um roteiro para transformar demanda, arquitetura, conteúdo, técnica e conversão em prioridades executáveis.",
    lead: "SEO conectado ao negócio começa por perguntas comerciais e termina em uma fila de decisões. Palavras-chave, correções técnicas e conteúdos são meios — não o objetivo final.",
    date: "28 de agosto de 2026",
    readTime: "7 min de leitura",
    sections: [
      {
        id: "estrategia",
        title: "Estratégia não é uma lista de palavras-chave",
        paras: [
          "Uma pesquisa de termos mostra como a demanda pode se expressar, mas não define sozinha onde a empresa deve competir. A decisão precisa considerar aderência da oferta, estágio da jornada, capacidade de produzir uma resposta melhor e possibilidade de medir valor.",
          "A estratégia organiza essas variáveis em temas, páginas, prioridades e hipóteses. Também registra o que não será feito agora — uma parte essencial da disciplina.",
        ],
      },
      {
        id: "diagnostico",
        title: "O diagnóstico separa sintoma, causa e oportunidade",
        paras: [
          "Queda de tráfego, baixa posição ou poucas conversões são sinais, não diagnósticos. A causa pode estar em indexação, arquitetura, intenção, concorrência, experiência, oferta ou mensuração.",
          "Um diagnóstico responsável cruza fontes e limita conclusões. Ele evita atribuir toda variação a uma única mudança e indica quais evidências ainda precisam ser coletadas.",
        ],
      },
      {
        id: "demanda",
        title: "Demanda precisa ser traduzida em arquitetura",
        paras: [
          "Temas de busca só ganham valor quando encontram um lugar claro no site. Isso exige decidir quais páginas devem existir, quais podem ser consolidadas e como links internos ajudam pessoas e mecanismos de busca a compreender relações.",
          "Arquitetura também é escolha comercial: ela define quais ofertas, problemas e estágios da jornada recebem prioridade.",
        ],
      },
      {
        id: "priorizacao",
        title: "Priorizar por impacto, esforço, dependência e risco",
        paras: [
          "Uma fila baseada apenas em volume de busca tende a ignorar implementação e conversão. Uma fila baseada apenas em facilidade pode acumular tarefas sem impacto.",
          "O roadmap deve combinar impacto potencial, esforço real, dependências técnicas, risco e tempo de aprendizagem. O critério precisa ser compreendido pelas equipes envolvidas.",
        ],
      },
      {
        id: "indicadores",
        title: "Indicadores devem responder perguntas",
        paras: [
          "Posição média, sessões e cliques podem ajudar, mas raramente explicam o cenário isoladamente. O acompanhamento precisa recortar temas, páginas estratégicas, saúde técnica, conversões e andamento da execução.",
          "O relatório ganha utilidade quando termina em decisão: manter, investigar, corrigir, consolidar, expandir ou aguardar mais evidência.",
        ],
      },
      {
        id: "ciclo",
        title: "SEO é um ciclo de aprendizagem",
        paras: [
          "A estratégia não termina quando o plano é entregue. Mudanças no site, na concorrência, na demanda e nos mecanismos de busca alteram hipóteses e prioridades.",
          "A maturidade aparece quando a organização registra o que mudou, valida implementações e usa aprendizados para melhorar o próximo ciclo.",
        ],
      },
    ],
  },
  {
    slug: "seo-tecnico",
    category: "SEO Técnico",
    title: "SEO técnico: o que priorizar antes de produzir mais conteúdo",
    excerpt: "Por que rastreamento, indexação, arquitetura e performance precisam entrar na ordem correta de execução.",
    lead: "Produzir mais conteúdo sobre uma base técnica instável pode ampliar custo sem ampliar descoberta. A prioridade correta começa por garantir que páginas importantes possam ser acessadas, interpretadas e entregues com qualidade.",
    date: "28 de agosto de 2026",
    readTime: "8 min de leitura",
    sections: [
      {
        id: "rastrear",
        title: "Primeiro: o mecanismo consegue rastrear o que importa?",
        paras: [
          "Links internos, status HTTP, robots.txt, parâmetros e arquitetura determinam como recursos e páginas são descobertos. Um rastreamento amplo não significa rastreamento eficiente.",
          "A auditoria precisa identificar páginas estratégicas órfãs, ciclos de redirecionamento, profundidade excessiva e espaços consumidos por URLs de baixo valor.",
        ],
      },
      {
        id: "indexar",
        title: "Depois: a página deve e consegue ser indexada?",
        paras: [
          "Canonicals, noindex, duplicidade, qualidade e sinais conflitantes influenciam a seleção de URLs. O objetivo não é indexar tudo, mas garantir que a versão correta e útil esteja disponível.",
          "Relatórios de cobertura precisam ser lidos por tipo de página e intenção. Uma exclusão pode ser erro, decisão correta ou consequência de baixa diferenciação.",
        ],
      },
      {
        id: "arquitetura",
        title: "Arquitetura antes de expansão",
        paras: [
          "Quando categorias, serviços e conteúdos não seguem uma lógica clara, novas publicações aumentam sobreposição e dispersam links internos.",
          "Definir taxonomia, padrões de URL e relações entre páginas reduz retrabalho e torna a expansão mais sustentável.",
        ],
      },
      {
        id: "performance",
        title: "Performance é experiência e eficiência",
        paras: [
          "Core Web Vitals ajudam a observar carregamento, resposta e estabilidade visual. Eles não substituem relevância, mas problemas severos afetam navegação, conversão e capacidade de entrega.",
          "A correção deve buscar a causa: imagem, JavaScript, fonte, servidor, layout ou terceiro. Otimizações superficiais podem melhorar uma nota sem resolver a experiência real.",
        ],
      },
      {
        id: "priorizar",
        title: "Nem todo erro tem o mesmo impacto",
        paras: [
          "Ferramentas frequentemente produzem centenas de alertas. A prioridade deve considerar número e importância das URLs afetadas, estágio da jornada, risco e dependência.",
          "Correções estruturais que afetam templates e páginas estratégicas costumam superar ajustes isolados de baixo alcance.",
        ],
      },
      {
        id: "validar",
        title: "Implementar sem validar mantém a incerteza",
        paras: [
          "Toda correção precisa de critério de aceite: status esperado, canonical correto, ausência de regressão, melhoria de tempo ou mudança no relatório de cobertura.",
          "Registre data, escopo e resultado técnico. Só depois observe efeitos orgânicos dentro de uma janela adequada.",
        ],
      },
    ],
  },
  {
    slug: "seo-local",
    category: "SEO Local",
    title: "SEO local: como organizar presença, relevância e confiança",
    excerpt: "Os pilares para uma presença regional consistente, útil e alinhada à operação real da empresa.",
    lead: "Presença local consistente depende de realidade operacional, informações confiáveis e relevância para a região. Criar páginas ou perfis em massa sem capacidade de sustentação tende a gerar fragilidade.",
    date: "28 de agosto de 2026",
    readTime: "6 min de leitura",
    sections: [
      {
        id: "base",
        title: "Comece pela base de informações",
        paras: [
          "Nome, categoria, endereço, telefone, horário e área de atendimento precisam representar a operação real e permanecer consistentes nos principais canais.",
          "Antes de otimizar, defina quem é responsável por atualizar mudanças e responder inconsistências.",
        ],
      },
      {
        id: "perfil",
        title: "O perfil local precisa servir ao usuário",
        paras: [
          "Categorias, serviços, fotos, descrição, perguntas e atualizações devem facilitar a decisão. Preencher todos os campos sem precisão é menos útil que manter informações essenciais corretas.",
          "A rotina de gestão precisa incluir monitoramento de alterações, mensagens e desempenho, respeitando as políticas da plataforma.",
        ],
      },
      {
        id: "paginas",
        title: "Páginas locais devem ter utilidade própria",
        paras: [
          "Uma página por cidade só faz sentido quando existe operação, demanda e conteúdo capaz de responder àquele contexto. Trocar apenas o nome da localidade cria páginas pouco diferenciadas.",
          "Informações sobre cobertura, logística, prova local e próximos passos ajudam a construir utilidade real.",
        ],
      },
      {
        id: "reputacao",
        title: "Reputação é sinal de confiança, não recurso manipulável",
        paras: [
          "Avaliações precisam refletir experiências reais. Compra, troca ou incentivo enganoso aumenta risco e reduz credibilidade.",
          "A empresa deve facilitar feedback legítimo, responder com respeito e usar padrões recorrentes para melhorar a operação.",
        ],
      },
      {
        id: "autoridade",
        title: "Relevância regional também acontece fora do perfil",
        paras: [
          "Menções, parceiros, entidades locais, conteúdo e links editoriais podem reforçar presença quando possuem relação real com a empresa e a comunidade.",
          "O objetivo não é acumular diretórios, mas construir um ecossistema coerente de informação e confiança.",
        ],
      },
      {
        id: "medicao",
        title: "Meça ações locais e resultados de negócio",
        paras: [
          "Visualizações e posições ajudam, mas ligações, rotas, mensagens, formulários e vendas atribuíveis aproximam a análise do negócio.",
          "Compare períodos e localidades com cuidado, considerando sazonalidade, mudanças operacionais e mídia paga.",
        ],
      },
    ],
  },
];
